import { ArrowRight01Icon } from '@hugeicons/react'
import clsx from 'clsx'
import type { ElementType, ReactNode } from 'react'

import type { ResourceStatus } from '@/shared/lib/status'

/* Cabeçalho único para os dois papéis que antes tinham quatro implementações
   divergentes: header de widget na home e separador de seção nas listagens.
   Ver `design.md §5.1`.

   As quatro que ele substitui divergiam em tamanho (`text-sm` vs `text-caps`),
   cor (`gray-900` / `gray-500` / `gray-700`), presença de contagem e
   espaçamento (`mb-5`/`mb-2.5` vs `mb-2 mt-4`).

   Tipografia: `text-2xs` (12px) em caixa alta. É EXCEÇÃO DELIBERADA à nota de
   escala do MASTER §3, que reserva `--text-caps` (10px) para caixa alta e
   registra que 12px em caixa alta já foi adotado e revertido. O caso revertido
   era chip de estado dentro de card, competindo com o nome do item; aqui o
   elemento é título de bloco, que deve dominar o conteúdo abaixo. Decisão do
   tech lead, registrada no MASTER. */

export type SectionHeaderTone = 'default' | 'inverse'

export type SectionHeaderProps = {
  /** Nível do heading. `h2` por padrão: `PageHeader` já ocupa o `h1`. */
  as?: ElementType
  /** Alvo do `aria-labelledby` de quem envolve a seção. */
  id?: string
  icon?: ReactNode
  title: string
  /** Contagem de itens da seção. `0` é exibido; `undefined` omite a pílula. */
  count?: number
  /**
   * Status da seção quando ela agrupa por `ResourceStatus` (visão "Todas").
   * Colore o contador com o mesmo token do `Badge` de item. Omitido: contador
   * cai no tom neutro de `tone` — caso da Agenda, que agrupa por tempo.
   */
  status?: ResourceStatus
  /** `inverse` para uso sobre o hero gradiente (texto claro). */
  tone?: SectionHeaderTone
  /** `SectionHeader.Action` — link de ação à direita. */
  children?: ReactNode
  className?: string
}

const TITLE_TONE: Record<SectionHeaderTone, string> = {
  default: 'text-gray-700',
  inverse: 'text-white/80'
}

const COUNT_TONE: Record<SectionHeaderTone, string> = {
  default: 'bg-gray-200 text-gray-700',
  inverse: 'bg-white/15 text-white'
}

/* Mesmos tokens do `Badge` de item (`shared/ui/Badge.tsx`), para o contador da
   seção falar a mesma cor-por-status. Número sempre branco por decisão do
   usuário — diferente do `Badge`, que troca pra `gray-900` em progress/locked/
   success/expired porque branco fica abaixo do piso de 4.5:1 do
   `ACCESSIBILITY.md §1.4` nesses quatro (medido: 2.16 / 2.45 / 3.30 / 3.40).
   Só `available` (roxo) passa com branco (5.50). Exceção deliberada, não
   omissão — registrada também no `design-system/MASTER.md`.

   `danger` fica sem entrada de propósito: nenhuma seção "Todas" agrupa por
   ele hoje. Se algum dia agrupar, cai no tom neutro em vez de quebrar. */
const COUNT_STATUS_BG: Partial<Record<ResourceStatus, string>> = {
  available: 'bg-[var(--gami-badge-available-bg)]',
  progress: 'bg-[var(--gami-badge-progress-bg)]',
  success: 'bg-[var(--gami-badge-success-bg)]',
  locked: 'bg-[var(--gami-badge-locked-bg)]',
  expired: 'bg-[var(--gami-badge-expired-bg)]'
}

export const resolveCountStatusClass = (
  status?: ResourceStatus
): string | undefined => (status ? COUNT_STATUS_BG[status] : undefined)

/**
 * Sem margem própria — o ritmo vertical é do container (MASTER §7 L4).
 */
export const SectionHeader = ({
  as: Heading = 'h2',
  id,
  icon,
  title,
  count,
  status,
  tone = 'default',
  children,
  className
}: SectionHeaderProps) => {
  const statusClass = resolveCountStatusClass(status)

  return (
    <div className={clsx('flex items-center justify-between gap-2', className)}>
      <Heading
        id={id}
        className={clsx(
          'm-0 flex items-center gap-1.5 text-2xs font-bold uppercase tracking-wide',
          TITLE_TONE[tone]
        )}
      >
        {icon ? (
          <span className="flex shrink-0 items-center" aria-hidden>
            {icon}
          </span>
        ) : null}
        {title}
        {count !== undefined ? (
          <span
            className={clsx(
              /* `rounded-sm` (`--radius-sm`), não `rounded-full`: MASTER §7 L1
                 reserva 999px para pílula de 20-24px; aqui o pedido é quadrado
                 com canto levemente arredondado, não pílula. */
              'ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-sm px-1 text-2xs font-bold leading-none',
              statusClass ? [statusClass, 'text-white'] : COUNT_TONE[tone]
            )}
          >
            {count}
          </span>
        ) : null}
      </Heading>

      {children}
    </div>
  )
}

export type SectionHeaderActionProps = {
  onClick?: () => void
  href?: string
  children: ReactNode
  className?: string
}

/**
 * Link de ação no header — o "Ver todos".
 *
 * Herda de `ObjectivesCard` o tamanho, o peso, a cor e o ícone: aquele CTA é a
 * referência aprovada. **Não** herda o `py-3`, que existe lá por ser rodapé em
 * largura total; aqui seria altura demais para uma linha de header. `py-1.5`
 * sobre 12px de texto dá ~24px, o piso de alvo de toque (ACCESSIBILITY §1.1).
 *
 * `--color-brand` e não `--color-brand-text`: é a cor exata do CTA de
 * referência, e brand passa como texto com 5.50 (ACCESSIBILITY §1.3).
 */
const SectionHeaderAction = ({
  onClick,
  href,
  children,
  className
}: SectionHeaderActionProps) => {
  const classes = clsx(
    'flex shrink-0 cursor-pointer items-center gap-0.5 border-0 bg-transparent px-0 py-1.5',
    'text-2xs font-bold text-[var(--color-brand)] no-underline',
    'transition-opacity hover:opacity-70',
    className
  )

  const content = (
    <>
      {children}
      <ArrowRight01Icon size={13} aria-hidden />
    </>
  )

  if (href) {
    return (
      <a href={href} className={classes}>
        {content}
      </a>
    )
  }

  return (
    <button type="button" onClick={onClick} className={classes}>
      {content}
    </button>
  )
}

SectionHeader.Action = SectionHeaderAction
SectionHeader.displayName = 'SectionHeader'
